import { useCallback, useState, useEffect, useRef } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppSelector, useAppDispatch, CollectionFilesyncAPI, useSendRequestMutation, DriveRequest, ErrorStackItem, Message, getEnumKey, MessageHandlerDisplayType,
  DriveWatchedErrorMessages, EntryItemBase, COLLECTION_PROTOCOL_VERSIONS, useGetGrants, useGetPermissions, PermissionSetType, GrantSetType, GenericEntryEntity, GenericEntryMap
} from "src/common";
import { uiActions, driveActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

interface WatchDataType extends EntryItemBase {
    collection_protocol_version: COLLECTION_PROTOCOL_VERSIONS;
    watch: boolean;
}

export function useWatchNode(current_account: Account) {
    const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
    const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
    const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
    const { grants, getGrants, error: grant_error, destroyGrants } = useGetGrants(current_account);
    const [data, setData] = useState<WatchDataType>();
    const [bulk_action_errors, setBulkActionErrors] = useState<string[]>([]);
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [sendRequest] = useSendRequestMutation();
    const dispatch = useAppDispatch();
    const bulk_unwatch_ref = useRef<GenericEntryMap>(new Map<string, GenericEntryEntity>);
    const bulk_action_errors_ref = useRef<string[]>([]);

    // Description: Add the missing reader grants to the active grants object 
    useEffect(() => {
        if (!!permissions && !!data) {
            (data.collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V1 ||
                data.collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V2 && grants) &&
                handleWatchNode(data, permissions, grants);
        }
    }, [grants, permissions, grant_error]);

    // Description: Set Node and descendants to watch OR unwatch depending on the watch flag
    async function handleWatchNode(_data: WatchDataType, _permissions: PermissionSetType, _grants?: GrantSetType) {
        const id = _data.id;
        const drive_request = await CollectionFilesyncAPI.setWatchNode(current_account, _data.collection_id, id, _permissions, _grants);
        async function callback(message: FSMessage) {
            (message.getStatus() === FSStatus.OK) ?
                setSuccess(true) :
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_setting_node_watch }, message,
                    MessageHandlerDisplayType.toastr);
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_setting_node_watch);
    }

    // Description: Set Node and descendants to watch OR unwatch depending on the watch flag
    async function handleUnwatchNode(collection_id: string, id: string, is_bulk: boolean = false) {
        const drive_request = await CollectionFilesyncAPI.setUnwatchNode(current_account, collection_id, id);
        async function callback(message: FSMessage) {
            const entry = bulk_unwatch_ref.current.get(id);
            !!entry && bulk_unwatch_ref.current.delete(id);
            const size = bulk_unwatch_ref.current.size;
            if (message.getStatus() === FSStatus.OK) {
                !is_bulk && setSuccess(true);
            } else {
                is_bulk ? bulk_action_errors_ref.current.push(entry?.name || id) :
                    handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_setting_node_watch }, message,
                        MessageHandlerDisplayType.toastr);
            }
            // NOTE: complete bulk callbacks
            if (is_bulk && size <= 0) {
                setBulkActionErrors(bulk_action_errors_ref.current);
                setSuccess(true);
            };
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_setting_node_watch);
    }

    // Description: Remove ALL Watched rules
    async function handleClearAllWatched() {
        const drive_request = await CollectionFilesyncAPI.clearAllWatched(current_account);
        async function callback(message: FSMessage) {
            (message.getStatus() === FSStatus.OK) ?
                setSuccess(true) :
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_setting_node_watch }, message,
                    MessageHandlerDisplayType.toastr);
        }
        handleSendRequest(drive_request, callback, DriveWatchedErrorMessages.error_setting_node_watch);
    }

    // -----------------------------
    // HOOK General
    // -----------------------------
    // Description: Sends Request and handles error when the request object is null, pass it custom stack message
    function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveWatchedErrorMessages.error_sending_request) {
        if (!!request) {
            request.setCallback(callback);
            sendRequest(request);
        } else {
            handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_error, stack_message: DriveWatchedErrorMessages.error_sending_request });
        }
    }
    // Description:  send error to store
    function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useEmailFrequency Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setError(true);
    }

    // -----------------------------
    // Callbacks
    // -----------------------------
    // Description: Call hook for watching / unwatching nodes and descendants
    const setNodeWatchState = useCallback((entry: EntryItemBase, watch: boolean, collection_protocol_version: COLLECTION_PROTOCOL_VERSIONS) => {
        dispatch(driveActions.setLoading(true));
        const _data = { ...entry, ...{ collection_protocol_version, watch } };
        if (watch) {
            setData(_data);
            getPermissions(entry.collection_id, default_permissions);
            (collection_protocol_version === COLLECTION_PROTOCOL_VERSIONS.V2 && !!entry.maintainer_id) &&
                getGrants(entry.maintainer_id, entry.collection_id, default_permissions, default_grants);
        } else {
            handleUnwatchNode(_data.collection_id, _data.id);
        }
    }, []);

    // Description: Call hook for watching / unwatching nodes and descendants
    const setBulkUnWatchState = useCallback((entries: GenericEntryEntity[]) => {
        dispatch(driveActions.setLoading(true));
        _.map(entries, (entry: GenericEntryEntity) => {
            bulk_unwatch_ref.current.set(entry.id, entry);
            handleUnwatchNode(entry.collection_id, entry.id, true);
        });
    }, []);

    // Description: Call hook for watching / unwatching nodes and descendants
    const clearAllWatchedNodes = useCallback(() => {
        handleClearAllWatched();
    }, []);


    // Description: Reset all from component
    const reset = useCallback(() => {
        setSuccess(false);
        setError(false);
        setData(undefined);
        setBulkActionErrors([]);
        destroyPermissions();
        destroyGrants();
        bulk_unwatch_ref.current = new Map<string, GenericEntryEntity>;
        bulk_action_errors_ref.current = [];
    }, []);

    return {
        setNodeWatchState,
        setBulkUnWatchState,
        clearAllWatchedNodes,
        reset,
        success,
        error,
        bulk_action_errors
    };
}
